import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ImageService } from '../../services/image/image.service';
import { ScriptService } from '../../services/load.script/load.script.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { plainTextToHtml, richHtmlToPlainText } from '../../utils/html.utils';
import { SUN_EDITOR_CSS_URL, SUN_EDITOR_SCRIPT_URL } from 'src/app/common/constants/sun.editor';
import {
  fillSunEditorIframe,
  readSunEditorEnvironment,
  shouldUseSunEditorIframe,
  SUN_EDITOR_IFRAME_HEIGHT,
} from 'src/app/common/utils/sun.editor.utils';

const SUN_EDITOR_LOAD_TIMEOUT_MS = 10000;

let sunEditorLoader: Promise<boolean> | null = null;
const sunEditorCssLoaders = new WeakMap<Document | ShadowRoot, Promise<boolean>>();

function loadSunEditorCss(host: Node | undefined): Promise<boolean> {
  const rootNode = host?.getRootNode();
  const root: Document | ShadowRoot = rootNode instanceof ShadowRoot ? rootNode : document;
  const cached = sunEditorCssLoaders.get(root);
  if (cached) return cached;

  const loader = new Promise<boolean>((resolve) => {
    let link = root.querySelector<HTMLLinkElement>(`link[rel="stylesheet"][href="${SUN_EDITOR_CSS_URL}"]`);
    if (link?.sheet) {
      resolve(true);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const finish = (loaded: boolean) => {
      clearTimeout(timer);
      if (!loaded) sunEditorCssLoaders.delete(root);
      resolve(loaded);
    };

    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SUN_EDITOR_CSS_URL;
      (root instanceof ShadowRoot ? root : document.head).appendChild(link);
    }
    const loadingLink = link;
    loadingLink.addEventListener('load', () => finish(true));
    loadingLink.addEventListener('error', () => {
      loadingLink.remove();
      finish(false);
    });
    timer = setTimeout(() => finish(!!loadingLink.sheet), SUN_EDITOR_LOAD_TIMEOUT_MS);
  });

  sunEditorCssLoaders.set(root, loader);
  return loader;
}

function loadSunEditor(): Promise<boolean> {
  if (window['SUNEDITOR']) return Promise.resolve(true);
  if (sunEditorLoader) return sunEditorLoader;

  sunEditorLoader = new Promise<boolean>((resolve) => {
    let done = false;
    let poll: ReturnType<typeof setInterval>;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (loaded: boolean) => {
      if (done) return;
      done = true;
      clearInterval(poll);
      clearTimeout(timer);
      if (!loaded) sunEditorLoader = null;
      resolve(loaded);
    };

    let script = document.querySelector<HTMLScriptElement>(`script[src="${SUN_EDITOR_SCRIPT_URL}"]`);
    // Async scripts delay the window load event, so an existing tag without SUNEDITOR after load has failed.
    if (script && document.readyState === 'complete') {
      script.remove();
      script = null;
    }
    if (!script) {
      script = document.createElement('script');
      script.src = SUN_EDITOR_SCRIPT_URL;
      script.async = true;
      document.head.appendChild(script);
    }
    const loadingScript = script;
    loadingScript.addEventListener('load', () => finish(!!window['SUNEDITOR']));
    loadingScript.addEventListener('error', () => {
      loadingScript.remove();
      finish(false);
    });

    // An existing <script> may have finished loading before the listeners were attached.
    poll = setInterval(() => window['SUNEDITOR'] && finish(true), 200);
    timer = setTimeout(() => finish(!!window['SUNEDITOR']), SUN_EDITOR_LOAD_TIMEOUT_MS);
  });

  return sunEditorLoader;
}

@Component({
  selector: 'ig-rich-text-box',
  templateUrl: './rich.text.box.component.html',
  styleUrls: ['./rich.text.box.component.scss'],
  host: {
    class: 'z-10',
  },
})
export class RichTextBoxComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public description: string;
  @Output() public descriptionChange = new EventEmitter<string>();
  @Output() public editorBlur = new EventEmitter<void>();

  descriptionEditor: any;
  isEditDescription = false;
  isDescriptionNeedShowMore = false;
  // Plain textarea used when SunEditor can't be loaded (e.g. CDN blocked), so the form stays usable.
  isFallbackMode = false;
  isEditorPending = true;
  private fallbackContent = '';
  private isDestroyed = false;

  imageService = inject(ImageService);
  scriptService = inject(ScriptService);
  private cdRef = inject(ChangeDetectorRef);

  @ViewChild('descriptionTextarea') public descriptionTextarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('descriptionHtml') public descriptionHtml: ElementRef;
  @ViewChild('menuTrigger') public menuTrigger: MatMenuTrigger;

  constructor() {}

  ngOnInit() {
    this.adjustTextareaHeight();
  }

  ngAfterViewInit() {
    this.setUpHtmlEditor();
  }

  ngOnDestroy() {
    this.isDestroyed = true;
  }

  setUpHtmlEditor() {
    Promise.all([loadSunEditor(), loadSunEditorCss(this.descriptionTextarea?.nativeElement)]).then(([scriptLoaded, cssLoaded]) => {
      if (this.isDestroyed || !this.descriptionTextarea) return;
      this.isEditorPending = false;
      this.cdRef.detectChanges();
      if (!scriptLoaded || !cssLoaded) {
        console.warn(
          `RichTextBox: SunEditor ${scriptLoaded ? 'stylesheet' : 'script'} could not be loaded, using plain textarea.`
        );
        this.enableFallbackMode();
        return;
      }
      try {
      const useIframe = shouldUseSunEditorIframe(
        readSunEditorEnvironment(this.descriptionTextarea.nativeElement.getRootNode()),
      );
      const layoutOptions = useIframe
        ? {
            iframe: true,
            iframeCSSFileName: [SUN_EDITOR_CSS_URL],
            // The page's font override cannot reach into the iframe, so set it on the body.
            defaultStyle: "font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;",
            width: 'auto',
            height: SUN_EDITOR_IFRAME_HEIGHT,
            minHeight: '100px',
          }
        : {
            width: 'auto',
            height: 'auto',
            minHeight: '100px',
            maxHeight: '1500px',
          };
      this.descriptionEditor = window['SUNEDITOR'].create(this.descriptionTextarea.nativeElement, {
        showPathLabel: false,
        charCounter: true,
        ...layoutOptions,
        value: this.description || '',
        buttonList: [
          ['undo', 'redo', 'font', 'fontSize', 'formatBlock'],
          ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'removeFormat'],
          [
            'fontColor',
            'hiliteColor',
            'outdent',
            'indent',
            'align',
            'horizontalRule',
            'list',
            'table',
          ],
          ['link', 'image', 'video', 'fullScreen', 'showBlocks', 'codeView', 'preview', 'print'],
        ],
      });
      if (useIframe) {
        fillSunEditorIframe(this.descriptionEditor);
      }

      this.descriptionEditor.onImageUploadBefore = (files, info, core, uploadHandler) => {
        try {
          this.handleImageUploadBefore(files, info, uploadHandler);
        } catch (err) {
          uploadHandler(err.toString());
        }
      };

      this.descriptionEditor.onChange = (contents) => {
        this.descriptionChange.emit(contents);
      };

      this.descriptionEditor.onBlur = () => {
        this.editorBlur.emit();
      };
      } catch (err) {
        console.error('RichTextBox: SunEditor initialization failed:', err);
        this.enableFallbackMode();
      }
    });
  }

  onFallbackInput() {
    if (!this.isFallbackMode) return;
    this.fallbackContent = plainTextToHtml(this.descriptionTextarea.nativeElement.value);
    this.descriptionChange.emit(this.fallbackContent);
  }

  onFallbackBlur() {
    if (!this.isFallbackMode) return;
    this.editorBlur.emit();
  }

  private enableFallbackMode() {
    this.isFallbackMode = true;
    this.fallbackContent = this.description || '';
    this.descriptionTextarea.nativeElement.value = richHtmlToPlainText(this.fallbackContent);
  }

  getEditorContent(): string {
    if (this.descriptionEditor) {
      return this.descriptionEditor.getContents();
    }
    if (this.isFallbackMode) {
      return this.fallbackContent;
    }
    // Fallback: return raw textarea value if the rich editor never initialized
    if (this.descriptionTextarea?.nativeElement) {
      return this.descriptionTextarea.nativeElement.value;
    }
    return '';
  }

  setEditorContent(content: string): void {
    if (this.descriptionEditor) {
      this.descriptionEditor.setContents(content);
    } else if (this.isFallbackMode) {
      this.fallbackContent = content || '';
      this.descriptionTextarea.nativeElement.value = richHtmlToPlainText(this.fallbackContent);
    }
  }

  private adjustTextareaHeight() {
    setTimeout(() => {
      if (this.descriptionTextarea && this.descriptionTextarea.nativeElement) {
        this.descriptionTextarea.nativeElement.style.height =
          Math.min(this.descriptionTextarea.nativeElement.scrollHeight, 300) + 'px';
      }
      if (this.descriptionHtml) {
        this.isDescriptionNeedShowMore = this.descriptionHtml.nativeElement.scrollHeight > 150;
      }
    });
  }

  private handleImageUploadBefore(files, info, uploadHandler) {
    this.imageService
      .getBase64ByImageFile(files[0])
      .then((imageInfo) => {
        if (imageInfo) {
          this.imageService.uploadBase64ImageToAWS(imageInfo).subscribe((res) => {
            if (res.success) {
              const response = {
                result: [
                  {
                    url: `${res.url}`,
                    name: files[0].name,
                    size: files[0].size,
                  },
                ],
              };
              uploadHandler(response);
            } else {
              uploadHandler('upload image fail');
            }
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }
}
